import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { purgeLegacyAuthStorage } from '@/lib/api';
import { olvidarMarca } from '@/lib/marca';
import { olvidarPermisos } from '@/lib/permisos';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonationSession, setImpersonationSession] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      purgeLegacyAuthStorage();
      const { data } = await api.get('/auth/me');
      setUser(data);
      try {
        const impRes = await api.get('/impersonation/active');
        setImpersonationSession(impRes.data.session);
      } catch { /* no imp session */ }
    } catch {
      setUser(null);
      purgeLegacyAuthStorage();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, turnstileToken) => {
    const { data } = await api.post('/auth/login', {
      email, password, turnstile_token: turnstileToken || undefined,
    });
    // MFA: no tokens stored — challenge only
    if (data.mfa_required) return data;
    // Evita que el logo/título del tenant anterior se quede en la pestaña.
    olvidarMarca({ revalidar: false });
    olvidarPermisos();
    setUser(data.user);
    purgeLegacyAuthStorage();
    return data;
  };

  const verifyMfaLogin = async (challengeToken, code) => {
    const { data } = await api.post('/auth/mfa/login-verify', {
      challenge_token: challengeToken, code,
    });
    olvidarMarca({ revalidar: false });
    olvidarPermisos();
    setUser(data.user);
    purgeLegacyAuthStorage();
    return data;
  };

  const register = async (email, password, name, turnstileToken) => {
    const { data } = await api.post('/auth/register', {
      email, password, name, turnstile_token: turnstileToken || undefined,
    });
    olvidarMarca({ revalidar: false });
    olvidarPermisos();
    setUser(data.user);
    purgeLegacyAuthStorage();
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignore */ }
    olvidarMarca({ revalidar: false });
    olvidarPermisos();
    setUser(null);
    setImpersonationSession(null);
    purgeLegacyAuthStorage();
    try {
      const bc = new BroadcastChannel('cavernicolapp-auth');
      bc.postMessage({ type: 'logout' });
      bc.close();
    } catch { /* ignore */ }
  };

  const startImpersonation = async (params) => {
    const { data } = await api.post('/impersonation/start', params);
    // S71: no access_token in JSON — cookie set by server
    olvidarMarca({ revalidar: false });
    olvidarPermisos();
    setImpersonationSession(data.session);
    setUser(data.impersonated_user);
    purgeLegacyAuthStorage();
    return data;
  };

  const endImpersonation = async () => {
    await api.post('/impersonation/end');
    olvidarMarca({ revalidar: false });
    olvidarPermisos();
    setImpersonationSession(null);
    await checkAuth();
  };

  const isPlatformUser = user?.user_type === 'platform';
  const isTenantUser = user?.user_type === 'tenant';
  const isAdmin = isPlatformUser && ['platform_owner_root', 'super_admin_platform', 'platform_admin'].includes(user?.platform_role);
  const isPlatformOwnerRoot = user?.platform_role === 'platform_owner_root';
  const isPlatformOwner = isPlatformUser && ['platform_owner_root', 'super_admin_platform'].includes(user?.platform_role);
  const platformModuleGrants = Array.isArray(user?.platform_module_grants) ? user.platform_module_grants : [];
  const canAccessAdminModule = (moduleKey) => {
    if (!isPlatformUser) return false;
    if (isPlatformOwner) return true;
    if (user?.platform_role === 'platform_admin') {
      return platformModuleGrants.includes(moduleKey);
    }
    return true;
  };
  const isImpersonating = !!impersonationSession;

  return (
    <AuthContext.Provider value={{
      user, loading, login, verifyMfaLogin, register, logout, checkAuth,
      isPlatformUser, isTenantUser, isAdmin, isPlatformOwnerRoot, isPlatformOwner,
      platformModuleGrants, canAccessAdminModule, isImpersonating,
      impersonationSession, startImpersonation, endImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
